import React, { useRef, useEffect } from 'react';

/**
 * A segmented one-time-code input.
 *
 * Extracted from the digit grid in MFAVerification.js and widened to accept
 * letters as well as digits, since verification codes are alphanumeric.
 * Characters are upper-cased on entry so the boxes always match what the client
 * is reading aloud.
 *
 * Presentational only - it holds no state and knows nothing about verification.
 *
 * @param {number} length      Number of boxes. Default 6.
 * @param {string} value       Current value; may be shorter than `length`.
 * @param {Function} onChange  Called with the new value on every edit.
 * @param {Function} onComplete Called with the value once all boxes are filled.
 * @param {boolean} disabled   Disables every box.
 * @param {boolean} invalid    Renders the boxes in an error state.
 * @param {boolean} autoFocus  Focus the first box on mount.
 *
 * Accepted characters are uppercase A-Z and 0-9, matching CODE_PATTERN in
 * backend/service_client_verification/models.py. Keep the two in step: a
 * character the boxes accept but the API rejects produces a complete-looking
 * entry that comes back malformed and consumes no attempt, which reads to the
 * advisor as "my wrong code was not counted".
 */
const CodeInput = ({
  length = 6,
  value = '',
  onChange,
  onComplete,
  disabled = false,
  invalid = false,
  autoFocus = false
}) => {
  const inputRefs = useRef([]);

  const isAllowed = (char) => /^[A-Z0-9]$/.test(char);

  const characters = Array.from({ length }, (_, i) => value[i] || '');

  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  const emit = (next) => {
    onChange?.(next);
    if (next.length === length && !next.includes(' ')) {
      onComplete?.(next);
    }
  };

  const setCharAt = (index, char) => {
    const next = characters.slice();
    next[index] = char;
    // Trim trailing blanks so a partial code stays a plain prefix string.
    return next.join('').replace(/\s+$/, '');
  };

  const handleChange = (index, raw) => {
    const char = (raw || '').slice(-1).toUpperCase();
    if (char && !isAllowed(char)) {
      // Ignore the keystroke outright. Accepting it would build an entry the
      // server must reject as malformed, which consumes no attempt and looks
      // to the advisor like a wrong code that was never counted.
      return;
    }
    const next = setCharAt(index, char || ' ');
    emit(next);
    if (char && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (characters[index]) {
        emit(setCharAt(index, ' '));
      } else if (index > 0) {
        emit(setCharAt(index - 1, ' '));
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasted = (event.clipboardData.getData('text') || '')
      .toUpperCase()
      .split('')
      .filter(isAllowed)
      .join('')
      .slice(0, length);
    if (!pasted) {
      return;
    }
    emit(pasted);
    inputRefs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className="d-flex justify-content-center gap-2">
      {characters.map((char, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          type="text"
          inputMode="text"
          maxLength={1}
          className={`form-control text-center fs-4 text-uppercase${invalid ? ' is-invalid' : ''}`}
          style={{ width: '50px', height: '60px', fontSize: '24px', fontWeight: 'bold' }}
          value={char.trim()}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          aria-label={`Verification code character ${index + 1}`}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};

export default CodeInput;
